/**
 * Service for creating notification email HTML templates
 */

/**
 * Creates HTML content for the couple confirmation email
 * @param coupleName1 - First partner's name
 * @param coupleName2 - Second partner's name
 * @returns HTML string for the confirmation email body
 */
export const createCoupleConfirmationEmailHtml = (
    coupleName1: string,
    coupleName2: string
): string => {
    const html = `
        <!DOCTYPE html>
        <html>
        <head>
            <style>
                body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
                .container { max-width: 700px; margin: 0 auto; padding: 20px; }
                .header { background-color: #C45526; color: white; padding: 30px 20px; border-radius: 5px 5px 0 0; text-align: center; }
                .content { background-color: #f9f9f9; padding: 30px 20px; border-radius: 0 0 5px 5px; }
                .greeting { font-size: 24px; font-weight: bold; color: #C45526; margin-bottom: 20px; text-align: center; }
                .message { font-size: 16px; line-height: 1.8; margin-bottom: 20px; }
                .highlight { background-color: #fff3cd; padding: 15px; border-left: 4px solid #C45526; margin: 20px 0; border-radius: 3px; }
                .footer { margin-top: 30px; text-align: center; font-size: 14px; color: #777; border-top: 2px solid #C45526; padding-top: 20px; }
                .emoji { font-size: 24px; }
                .signature { margin-top: 30px; font-style: italic; color: #555; }
            </style>
        </head>
        <body>
            <div class="container">
                <div class="header">
                    <div class="emoji">🎉 💍 ✨</div>
                    <h1 style="margin: 10px 0 0 0;">Your Wedding Website is Coming!</h1>
                </div>
                <div class="content">
                    <div class="greeting">
                        Dear ${coupleName1} & ${coupleName2},
                    </div>
                    
                    <div class="message">
                        Thank you for choosing <strong>Weddie.Site</strong> to create your wedding website! We're absolutely thrilled to be part of your special journey. 🥂
                    </div>
                    
                    <div class="message">
                        Your wedding website is <strong>so close to being live</strong>! Our team is already working on bringing your vision to life, and we can't wait for you and your guests to see it.
                    </div>
                    
                    <div class="highlight">
                        <strong>⏰ What happens next?</strong><br><br>
                        We'll have your beautiful, personalized wedding website ready within <strong>48 hours</strong>. 
                        You'll receive an email from us with:
                        <ul style="margin: 10px 0; padding-left: 20px;">
                            <li>Your website link</li>
                            <li>Instructions on how to share it with your guests</li>
                            <li>Any next steps for domain setup (if applicable)</li>
                        </ul>
                    </div>
                    
                    <div class="message">
                        In the meantime, feel free to relax knowing that your wedding website is in good hands. If you have any questions or need to make changes, don't hesitate to reach out to us!
                    </div>
                    
                    <div class="signature">
                        With excitement for your big day,<br>
                        <strong>The Weddie.Site Team</strong> 💕
                    </div>
                </div>
                <div class="footer">
                    <p><strong>Weddie.Site</strong> - Creating Beautiful Wedding Websites</p>
                    <p style="font-size: 12px; margin-top: 10px;">
                        This is an automated confirmation email. We'll be in touch soon with your website details!
                    </p>
                </div>
            </div>
        </body>
        </html>
    `;
    return html.replace(/\n/g, '');
};

/**
 * Creates HTML content for the wedding website request notification email
 * @param coupleName1 - First partner's name
 * @param coupleName2 - Second partner's name
 * @param branchName - GitHub branch name
 * @param domainType - Type of domain ('subdomain' or 'custom')
 * @param domainValue - The subdomain name or custom domain value
 * @param letUsBuyDomain - Whether Weddie should purchase the domain
 * @param confirmationDate - Date when the request was confirmed
 * @param dueDate - Date when the website should be delivered
 * @returns HTML string for the email body
 */
export const createNotificationEmailHtml = (
    coupleName1: string,
    coupleName2: string,
    branchName: string,
    domainType: string,
    domainValue: string,
    letUsBuyDomain: boolean,
    confirmationDate: Date,
    dueDate: Date
): string => {
    const githubLink = `https://github.com/Milszym/weddie-site/tree/${branchName}`;
    const configJsonLink = `https://github.com/Milszym/weddie-site/blob/${branchName}/src/mainConfig/config.json`;
    const imagesLink = `https://github.com/Milszym/weddie-site/tree/${branchName}/public/images`;
    const domainInfo = domainType === 'subdomain' 
        ? `Subdomain: ${domainValue}.weddie.site`
        : `Custom domain: ${domainValue} ${letUsBuyDomain ? '(Weddie will purchase the domain)' : '(Client will provide their own domain)'}`;
    
    const html = `
        <!DOCTYPE html>
        <html>
        <head>
            <style>
                body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
                .container { max-width: 700px; margin: 0 auto; padding: 20px; }
                .header { background-color: #C45526; color: white; padding: 20px; border-radius: 5px 5px 0 0; }
                .content { background-color: #f9f9f9; padding: 20px; border-radius: 0 0 5px 5px; }
                .field { margin-bottom: 20px; }
                .label { font-weight: bold; margin-bottom: 5px; color: #C45526; }
                .value { padding: 10px; background-color: white; border-radius: 3px; border: 1px solid #ddd; }
                .link { color: #C45526; text-decoration: none; }
                .link:hover { text-decoration: underline; }
                .highlight { background-color: #fff3cd; padding: 10px; border-left: 4px solid #ffc107; margin: 15px 0; }
                .footer { margin-top: 20px; font-size: 12px; color: #777; border-top: 1px solid #ddd; padding-top: 15px; }
            </style>
        </head>
        <body>
            <div class="container">
                <div class="header">
                    <h2>🎉 New Wedding Website Request</h2>
                </div>
                <div class="content">
                    <div class="field">
                        <div class="label">Couple:</div>
                        <div class="value">${coupleName1} & ${coupleName2}</div>
                    </div>
                    
                    <div class="field">
                        <div class="label">GitHub Branch:</div>
                        <div class="value">
                            <strong>${branchName}</strong><br>
                            <a href="${githubLink}" class="link">${githubLink}</a>
                        </div>
                    </div>
                    
                    <div class="field">
                        <div class="label">Quick Links:</div>
                        <div class="value">
                            📄 <a href="${configJsonLink}" class="link">Main Config JSON</a><br>
                            🖼️ <a href="${imagesLink}" class="link">Images Folder</a>
                        </div>
                    </div>
                    
                    <div class="field">
                        <div class="label">Domain Selection:</div>
                        <div class="value">${domainInfo}</div>
                    </div>
                    
                    <div class="highlight">
                        <strong>Timeline:</strong><br>
                        Confirmation Date: ${confirmationDate.toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' })}<br>
                        <strong>Due Date: ${dueDate.toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' })}</strong>
                    </div>
                </div>
                <div class="footer">
                    <p>This notification was automatically sent from the Weddie.Site questionnaire system.</p>
                </div>
            </div>
        </body>
        </html>
    `;
    return html.replace(/\n/g, '');
};
